import React, {useCallback, useEffect, useMemo, useState} from 'react'
import QRCode from 'react-qr-code'
import styled from 'styled-components'
import {
  INITIALIZE_QRIS_DATA,
  GIFTSHOP_TRANSACTION_PAYMENT_QRIS_TIME_LIMIT,
  WINDOW_MODE_MOBILE_WIDTH,
} from 'consts'
import {useTranslation} from 'i18n'
import {
  calcTimeDiff,
  downloadImages,
  formatHours,
  getCurrencyValue,
  parseDate,
  showSnackbar,
  useLocation,
} from 'utils'
import {Button, Icon, Paragraph} from 'common/components'
import firebase from 'lib/firebase'
import convertUnit from 'lib/unit'
import {useSelector} from 'lib/redux'
import {QrisOutletData} from 'types'

const StyledColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledRowContainer = styled.div`
  display: flex;
  flex-direction: row;
`

const StyledContainer = styled(StyledColumnContainer)`
  gap: ${convertUnit(12)};
  padding: ${convertUnit(20)};
  overflow-y: scroll;
  scrollbar-width: none;

  @media (min-width: ${WINDOW_MODE_MOBILE_WIDTH}px) {
    flex-direction: row;
  }
`

const StyledTransactionTimeContainer = styled(StyledColumnContainer)`
  justify-content: flex-start;
  align-items: flex-start;
`

const StyledTransactionTimeItemContainer = styled(StyledRowContainer)`
  gap: ${convertUnit(10)};
  align-items: center;
`

const StyledPaymentInfoItem = styled(StyledColumnContainer)`
  flex: 1;
`

const StyledPaymentInfoContainer = styled(StyledRowContainer)`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
`

const StyledQRContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: ${convertUnit(40)} 0;

  @media (min-width: ${WINDOW_MODE_MOBILE_WIDTH}px) {
    margin: ${convertUnit(20)} 0;
  }
`

const StyledButton = styled(Button)`
  width: '100%';
  cursor: pointer;
`

const StyledLeftSection = styled(StyledColumnContainer)`
  flex: 1;
  gap: ${convertUnit(12)};
`

const StyledPaymentInstructionContainer = styled(StyledColumnContainer)`
  gap: ${convertUnit(8)};
  margin-top: ${convertUnit(8)};
  flex: 1;
`

const StyledQrCode = styled(QRCode)`
  height: ${convertUnit(256)};
  width: ${convertUnit(256)};
  display: flex;
  align-self: center;
`

export default function PaymentCheckoutQrisDetail() {
  const {translate} = useTranslation()
  const {username} = useSelector('user')!
  const param = useLocation('giftshop_payment_qris_history').state
  const {transaction_time, total_payment, invoice_id} = param
  const nextDate = useMemo(() => new Date(transaction_time), [transaction_time])
  const expDate = useMemo(
    () =>
      new Date(
        nextDate.setMinutes(
          nextDate.getMinutes() + GIFTSHOP_TRANSACTION_PAYMENT_QRIS_TIME_LIMIT,
        ),
      ),
    [nextDate],
  )
  const [QRData, setQRData] = useState<QrisOutletData>(INITIALIZE_QRIS_DATA)
  const [currentDate, setCurrentDate] = useState(new Date())
  const [timeDiff, setTimeDiff] = useState(
    calcTimeDiff(currentDate, QRData.expiration_date || expDate.toISOString()),
  )

  const handleDownload = useCallback(() => {
    const svg = document.getElementById('qris_code')
    if (svg) {
      const svgData = new XMLSerializer().serializeToString(svg)
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')!
      const img = new Image()
      img.onload = () => {
        canvas.width = img.width
        canvas.height = img.height
        ctx.drawImage(img, 0, 0)
        const pngFile = canvas.toDataURL('image/png')
        const downloadLink = document.createElement('a')
        downloadLink.download = `${invoice_id}-qris`
        downloadLink.href = `${pngFile}`
        downloadImages([{name: downloadLink.download, url: downloadLink.href}])
        showSnackbar(translate('transaction:qrisDownloadSuccess'))
      }
      img.src = `data:image/svg+xml;base64,${window.btoa(svgData)}`
    }
  }, [invoice_id, translate])

  const handleFirestore = useCallback(
    () =>
      firebase
        .firestore()
        .collection(process.env.FIRESTORE_COLLECTION_PAYMENT!)
        .doc(invoice_id)
        .onSnapshot((snapshot) => {
          const data = snapshot.data()!
          if (data !== undefined) {
            setQRData(data.qr)
          }
        }),
    [invoice_id],
  )

  const handleRenderTransactionTime = useMemo(
    () => (
      <StyledTransactionTimeContainer>
        <Paragraph fontSize="m" fontWeight="bold">
          {translate('giftShop:transactionTime')}
        </Paragraph>
        <StyledTransactionTimeItemContainer>
          <Icon type="clock-ol" size={14} color="gray_5" />
          <Paragraph fontSize="m" color="gray_5">
            {parseDate(transaction_time, 'MMM DD, YYYY, HH:mm')}
          </Paragraph>
        </StyledTransactionTimeItemContainer>
        <StyledTransactionTimeItemContainer>
          <Icon type="clock" size={14} color="primary_5" />
          <Paragraph fontSize="m" fontWeight="bold" color="primary_5">
            {QRData.expiration_date
              ? parseDate(QRData.expiration_date, 'MMM DD, YYYY, HH:mm')
              : parseDate(expDate.toISOString(), 'MMM DD, YYYY, HH:mm')}
          </Paragraph>
          <Paragraph fontSize="m" color="primary_5">
            ({formatHours(timeDiff.hours, timeDiff.minutes, timeDiff.seconds)})
          </Paragraph>
        </StyledTransactionTimeItemContainer>
      </StyledTransactionTimeContainer>
    ),
    [
      QRData.expiration_date,
      expDate,
      timeDiff.hours,
      timeDiff.minutes,
      timeDiff.seconds,
      transaction_time,
      translate,
    ],
  )

  const handleRenderAccountName = useMemo(
    () => (
      <StyledPaymentInfoItem>
        <Paragraph fontSize="m" fontWeight="bold">
          {translate('giftShop:accountName')}
        </Paragraph>
        <Paragraph fontSize="m">{username}</Paragraph>
      </StyledPaymentInfoItem>
    ),
    [translate, username],
  )

  const handleRenderTotalPayment = useMemo(
    () => (
      <StyledPaymentInfoItem>
        <Paragraph fontSize="m" fontWeight="bold">
          {translate('giftShop:totalPayment')}
        </Paragraph>
        <Paragraph fontSize="m" fontWeight="bold" color="primary_5">
          {getCurrencyValue(total_payment)}
        </Paragraph>
      </StyledPaymentInfoItem>
    ),
    [total_payment, translate],
  )

  const handleRenderPaymentInfo = useMemo(
    () => (
      <StyledPaymentInfoContainer>
        {handleRenderAccountName}
        {handleRenderTotalPayment}
      </StyledPaymentInfoContainer>
    ),
    [handleRenderAccountName, handleRenderTotalPayment],
  )

  const handleRenderQRSection = useMemo(
    () => (
      <StyledQRContainer>
        <StyledQrCode id="qris_code" value={QRData.actions.qr_string} />
      </StyledQRContainer>
    ),
    [QRData.actions.qr_string],
  )

  const handleRenderButtonSection = useMemo(
    () => (
      <StyledButton
        label={translate('global:download')}
        onClick={handleDownload}
      />
    ),
    [handleDownload, translate],
  )

  const handleRenderPaymentInstruction = useMemo(
    () => (
      <StyledPaymentInstructionContainer>
        <Paragraph fontSize="m" fontWeight="bold">
          {translate('transaction:qrisInstructionTitle')}
        </Paragraph>

        <StyledColumnContainer>
          <Paragraph fontSize="s">
            {translate('transaction:qrisInstructionContentA')}
          </Paragraph>
          <Paragraph fontSize="s">
            {translate('transaction:qrisInstructionContentB')}
          </Paragraph>
          <Paragraph fontSize="s">
            {translate('transaction:qrisInstructionContentC')}
          </Paragraph>
          <Paragraph fontSize="s">
            {translate('transaction:qrisInstructionContentD')}
          </Paragraph>
          <Paragraph fontSize="s">
            {translate('transaction:qrisInstructionContentE')}
          </Paragraph>
        </StyledColumnContainer>
      </StyledPaymentInstructionContainer>
    ),
    [translate],
  )

  useEffect(handleFirestore, [handleFirestore])

  useEffect(() => {
    setTimeDiff(calcTimeDiff(currentDate, expDate.toISOString()))
  }, [currentDate, expDate])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentDate(new Date())
    }, 1000)

    return () => clearTimeout(timeoutId)
  }, [currentDate])

  return (
    <StyledContainer>
      <StyledLeftSection>
        {handleRenderTransactionTime}
        {handleRenderPaymentInfo}
        {handleRenderQRSection}
        {handleRenderButtonSection}
      </StyledLeftSection>
      {handleRenderPaymentInstruction}
    </StyledContainer>
  )
}
